/**
 * CRM-ROLE-VISIBILITY-DOCTRINE-V1 + OWNER-READ-DOWN -- the manager tracking
 * landing (`/dashboard/agm`) visibility gate.
 *
 * Pure by construction (no React, no fetch, no Supabase, no Date.now()) so the
 * access decision unit-tests without jsdom -- mirrors decideRoutingSettingsAccess
 * (work-routing-settings-view.ts) and decideLeadCockpitAccess (lead-cockpit-view.ts).
 *
 * WHY THIS EXISTS (doctrine rule 1 -- hierarchy read-down):
 *   The manager tracking landing is subordinate OPERATIONAL work (the item-9
 *   buckets + clinic x metric matrix + per-staff load rollup). By
 *   06-CRM-ROLE-VISIBILITY-DOCTRINE rule 1, the senior operational tier reads it
 *   DOWN by position: owner (top monitor) + the manager tier (gm_agm /
 *   regional_head, whose J2/J3 landing this IS). It was previously guarded by
 *   RequireNavSection section="agm", whose decision keys off me.navSections --
 *   the SAME array that drives the left-nav render -- so it could not admit the
 *   owner without dumping the whole AgmNav into the owner menu (a rule-3
 *   violation). This per-surface role-set gate DECOUPLES route visibility (rule
 *   1) from nav layout (rule 3): the owner reaches the landing here and gets ONE
 *   owner nav entry, never the whole AGM section.
 *
 * SEE-NOT-DO (doctrine rule 2): this is a READ gate ONLY. Membership decides who
 * the route RENDERS for, never what they may DO -- there is no claim / assign /
 * qualify / stage path on this surface. Every write affordance stays keyed off
 * its own capability signal + backend route auth.
 *
 * PHI (doctrine rule 4): the manager tracking payload is counts / staff labels /
 * clinic labels / urgency levels / derived ages only (a PHI-negative scan runs
 * on the backend result, api/routes/manager-tracking.ts). Hierarchy admits the
 * aggregate; it never admits a clinical body.
 *
 * Backend route auth (MANAGER_TRACKING_AUDIENCE_ROLES + requireAgmScope) remains
 * the real security boundary; this module only closes the UX gap. This set is the
 * FRONTEND TWIN of the backend MANAGER_TRACKING_AUDIENCE_ROLES
 * (api/routes/manager-tracking.ts) -- kept in lockstep by review.
 */

import type { DashboardMe } from '@/dashboard/types';

/**
 * The leadership TIER that may READ the manager tracking landing: owner (top
 * monitor) + gm_agm / regional_head (the Team-Leader tier whose landing this is).
 * A VISIBILITY set (doctrine rule 1), deliberately held DISTINCT from the
 * capability sets (rule 2) even though it coincides with CONFIGURE_ROUTING_ROLES
 * today -- the two are separate concerns and may diverge. Frontend twin of the
 * backend MANAGER_TRACKING_AUDIENCE_ROLES.
 */
export const MANAGER_TRACKING_AUDIENCE_ROLES: ReadonlySet<DashboardMe['role']> =
  new Set<DashboardMe['role']>(['owner', 'gm_agm', 'regional_head']);

export type ManagerTrackingAccessDecision =
  | { kind: 'wait' }
  | { kind: 'render' }
  | { kind: 'redirect'; to: string };

/**
 * Pure access decision for the manager-tracking route guard. Mirrors
 * decideRoutingSettingsAccess: wait while loading; redirect to /login on error or
 * no scope; redirect to the caller's own homePath when the role is outside the
 * leadership read tier; otherwise render.
 */
export function decideManagerTrackingAccess(state: {
  loading: boolean;
  error: string | null;
  me: DashboardMe | null;
}): ManagerTrackingAccessDecision {
  if (state.loading) return { kind: 'wait' };
  if (state.error || !state.me) {
    return { kind: 'redirect', to: '/login' };
  }
  if (!MANAGER_TRACKING_AUDIENCE_ROLES.has(state.me.role)) {
    return { kind: 'redirect', to: state.me.homePath };
  }
  return { kind: 'render' };
}
